import type { ReactNode } from 'react';
import type { SignalSeverity } from '../lib/types.js';
import { cx, MONO } from './classes.js';
import { SectionLabel } from './Card.js';
import { StatusGlyph } from './StatusGlyph.js';
import type { CheckStatus } from '../lib/types.js';

/** A right-rail block: uppercase label, then items. */
export function RailSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5">
      <SectionLabel>{label}</SectionLabel>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

/** Licence / screening line: glyph, title, mono detail. */
export function RailItem({
  status,
  title,
  detail,
  mono = false,
}: {
  status: CheckStatus;
  title: ReactNode;
  detail: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-btn border border-line bg-surface px-4 py-3">
      <StatusGlyph status={status} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[14px] font-semibold">{title}</span>
        <span className={cx('text-[12px] text-mute', mono && MONO)}>{detail}</span>
      </span>
    </div>
  );
}

const SEVERITY_DOT: Record<SignalSeverity, string> = {
  high: 'bg-crit',
  warn: 'bg-warn',
  info: 'bg-line',
};

/** A rule that fired, with its evidence. Severity is a plain dot before the
 * title (redesign artboards: no ribbons, no pills — dots and glyphs only). */
export function SignalLine({
  severity,
  title,
  detail,
}: {
  severity: SignalSeverity;
  title: ReactNode;
  detail: ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-0.5 rounded-btn border border-line bg-surface px-4 py-3"
      data-severity={severity}
    >
      <span className="flex items-center gap-2 text-[14px] font-semibold">
        <span
          className={cx('size-2 shrink-0 rounded-full', SEVERITY_DOT[severity])}
          aria-hidden="true"
        />
        {title}
      </span>
      <span className="text-[13px] text-mute">{detail}</span>
    </div>
  );
}

const DOT: Record<'warn' | 'good' | 'ink' | 'crit', string> = {
  warn: 'bg-warn',
  good: 'bg-good',
  ink: 'bg-ink',
  crit: 'bg-crit',
};

/** Recent-changes timeline entry (Profile artboard right rail). */
export function TimelineItem({
  when,
  tone,
  children,
  last = false,
}: {
  when: string;
  tone: keyof typeof DOT;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <div className="grid grid-cols-[14px_minmax(0,1fr)] gap-3.5">
      <span className="flex flex-col items-center">
        <span
          className={cx('mt-[5px] size-2.5 rounded-full', DOT[tone])}
          aria-hidden="true"
        />
        {!last ? <span className="mt-1 w-px grow bg-line" aria-hidden="true" /> : null}
      </span>
      <span className={cx('flex flex-col gap-0.5', !last && 'pb-[18px]')}>
        <span className={cx(MONO, 'text-[12px] text-mute')}>{when}</span>
        <span className="text-[14px]">{children}</span>
      </span>
    </div>
  );
}
